import React, { useState, useEffect, useMemo, useCallback } from 'react';
import styled from 'styled-components';
import {
  workAssignmentAPI,
  userAPI,
  branchAPI,
  holidayGroupAPI,
  holidayAPI,
  leaveAPI,
} from '../services/api';
import { Card, PageHeader, ModernButton, ModernSelect, ModernInput, LoadingSpinner, Badge } from '../components/SharedComponents';
import {
  FiTrendingUp, FiRefreshCw, FiMapPin, FiUsers, FiClock, FiGift, FiNavigation,
  FiSearch, FiArrowLeft, FiAlertTriangle, FiStar, FiCalendar,
} from '../components/Icons';

/* ------------------------------------------------------------------ *
 *  Read-only analytics.  Every figure below is derived from existing  *
 *  GET endpoints (work-assignments / users / branches / holiday-      *
 *  groups / holidays).  No writes, no schema, no DB changes.          *
 * ------------------------------------------------------------------ */

// Canonical role -> display category map (mirrors BirdEyeView so the
// staffing rule matches the rest of the app: each shift needs >=1
// makeup + >=1 photographer).
const ROLE_TO_CATEGORY = {
  store_manager: 'manager',
  manager_assistant: 'manager',
  makeup: 'makeup',
  makeup_supervisor: 'makeup',
  photographer: 'photographer',
  photographer_supervisor: 'photographer',
  retoucher: 'retoucher',
  retoucher_supervisor: 'retoucher',
};

const CATEGORY_META = {
  manager: { label: 'Manager', short: 'M', color: '#e91e63' },
  makeup: { label: 'Makeup', short: 'MU', color: '#9c27b0' },
  photographer: { label: 'Photographer', short: 'P', color: '#2196f3' },
  retoucher: { label: 'Retoucher', short: 'R', color: '#4caf50' },
};

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const pad2 = (n) => String(n).padStart(2, '0');
const fmt = (n) => (n == null ? 0 : n).toLocaleString();

// Branches excluded from every Summary calculation (placeholder / non-operating
// branches that would otherwise skew analytics). Matched by name, case-insensitive.
const EXCLUDED_BRANCH_NAMES = ['HO'];
const isExcludedBranch = (name) => EXCLUDED_BRANCH_NAMES.some((x) => x.toUpperCase() === (name || '').trim().toUpperCase());

// Pull every assignment in a date range, paginating so we never hit the
// backend's default 3000-row cap and silently truncate the numbers.
async function fetchAllAssignments(start, end) {
  const pageSize = 2000;
  let skip = 0;
  let all = [];
  for (let i = 0; i < 100; i += 1) {
    const res = await workAssignmentAPI.getAll({
      start_date: start,
      end_date: end,
      skip,
      limit: pageSize,
    });
    const rows = res.data || [];
    all = all.concat(rows);
    if (rows.length < pageSize) break;
    skip += pageSize;
  }
  return all;
}

// Longest run of consecutive calendar days across a set of YYYY-MM-DD strings.
function maxConsecutive(dateStrings) {
  const uniq = Array.from(new Set(dateStrings)).sort();
  if (uniq.length === 0) return 0;
  let best = 1;
  let run = 1;
  for (let i = 1; i < uniq.length; i += 1) {
    const prev = new Date(`${uniq[i - 1]}T00:00:00`);
    const cur = new Date(`${uniq[i]}T00:00:00`);
    const diff = Math.round((cur - prev) / 86400000);
    if (diff === 1) { run += 1; best = Math.max(best, run); } else { run = 1; }
  }
  return best;
}

// Human tenure from an ISO created_at to now.
function tenureLabel(createdAt) {
  if (!createdAt) return '—';
  const start = new Date(createdAt);
  if (Number.isNaN(start.getTime())) return '—';
  const months = Math.max(0, Math.floor((Date.now() - start.getTime()) / (30.44 * 86400000)));
  const y = Math.floor(months / 12);
  const m = months % 12;
  if (y > 0) return `${y}y ${m}m`;
  return `${m}m`;
}

/* ----------------------------- styles ----------------------------- */

const PageContainer = styled.div`
  width: 100%;
  margin: 0;
  padding: ${(p) => p.theme.spacing.lg};
  box-sizing: border-box;
  max-width: none;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${(p) => p.theme.spacing.lg};
  flex-wrap: wrap;

  h1 {
    display: flex;
    align-items: center;
    gap: ${(p) => p.theme.spacing.sm};
  }
`;

const Controls = styled.div`
  display: flex;
  gap: ${(p) => p.theme.spacing.md};
  align-items: flex-end;
  flex-wrap: wrap;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;

  label {
    font-size: 0.72rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: ${(p) => p.theme.colors.text.secondary};
  }
`;

const Segmented = styled.div`
  display: inline-flex;
  border: 1px solid ${(p) => p.theme.colors.border.medium};
  border-radius: ${(p) => p.theme.borderRadius.medium};
  overflow: hidden;

  button {
    border: none;
    background: ${(p) => p.theme.colors.surface};
    color: ${(p) => p.theme.colors.text.secondary};
    padding: 9px 16px;
    font-size: 0.9rem;
    font-weight: 600;
    font-family: ${(p) => p.theme.fonts.main};
    cursor: pointer;
    transition: background 0.15s ease, color 0.15s ease;

    & + button {
      border-left: 1px solid ${(p) => p.theme.colors.border.medium};
    }

    &.active {
      background: ${(p) => p.theme.colors.primary};
      color: ${(p) => p.theme.colors.text.inverse};
    }
  }
`;

const KpiGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(190px, 1fr));
  gap: ${(p) => p.theme.spacing.md};
  margin: ${(p) => p.theme.spacing.xl} 0;
`;

const StatCard = styled(Card)`
  display: flex;
  flex-direction: column;
  gap: 4px;

  .value {
    font-size: 1.9rem;
    font-weight: 700;
    letter-spacing: -0.02em;
    color: ${(p) => p.accent || p.theme.colors.text.primary};
    line-height: 1.1;
  }

  .label {
    font-size: 0.82rem;
    font-weight: 600;
    color: ${(p) => p.theme.colors.text.secondary};
  }

  .sub {
    font-size: 0.75rem;
    color: ${(p) => p.theme.colors.text.light};
  }
`;

const Section = styled(Card)`
  margin-bottom: ${(p) => p.theme.spacing.xl};
`;

const SectionHead = styled.div`
  display: flex;
  align-items: center;
  gap: ${(p) => p.theme.spacing.sm};
  margin-bottom: 4px;

  h2 {
    margin: 0;
    font-size: 1.15rem;
    font-weight: 700;
    display: flex;
    align-items: center;
    gap: ${(p) => p.theme.spacing.sm};
  }

  svg {
    color: ${(p) => p.theme.colors.primary};
  }
`;

const SectionSub = styled.p`
  margin: 0 0 ${(p) => p.theme.spacing.lg} 0;
  font-size: 0.85rem;
  color: ${(p) => p.theme.colors.text.secondary};
`;

const TableWrap = styled.div`
  overflow: auto;
  max-height: ${(p) => p.maxHeight || 'none'};
  border-radius: ${(p) => p.theme.borderRadius.medium};
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.9rem;

  th, td {
    padding: 9px 12px;
    text-align: left;
    border-bottom: 1px solid ${(p) => p.theme.colors.border.light};
    white-space: nowrap;
  }

  th {
    position: sticky;
    top: 0;
    background: ${(p) => p.theme.colors.surfaceAlt};
    color: ${(p) => p.theme.colors.text.secondary};
    font-weight: 600;
    font-size: 0.72rem;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    z-index: 1;
  }

  td {
    color: ${(p) => p.theme.colors.text.primary};
  }

  td.num, th.num {
    text-align: right;
    font-variant-numeric: tabular-nums;
  }

  tbody tr:hover {
    background: ${(p) => p.theme.colors.surfaceAlt};
  }

  tbody tr.idle td {
    color: ${(p) => p.theme.colors.text.light};
  }
`;

const BarTrack = styled.div`
  height: 8px;
  min-width: 90px;
  background: ${(p) => p.theme.colors.surfaceAlt};
  border: 1px solid ${(p) => p.theme.colors.border.light};
  border-radius: 999px;
  overflow: hidden;
`;

const BarFill = styled.div`
  height: 100%;
  border-radius: 999px;
  transition: width 0.3s ease;
`;

const Dot = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  color: #fff;
  font-size: 0.68rem;
  font-weight: 700;
  flex-shrink: 0;
`;

const RoleName = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
`;

const Legend = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${(p) => p.theme.spacing.lg};
  margin-bottom: ${(p) => p.theme.spacing.lg};

  .item {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 0.82rem;
    color: ${(p) => p.theme.colors.text.secondary};
    font-weight: 500;
  }
`;

const CoverageGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: ${(p) => p.theme.spacing.lg};
`;

const CoverageCard = styled.div`
  border: 1px solid ${(p) => p.theme.colors.border.light};
  border-radius: ${(p) => p.theme.borderRadius.large};
  padding: ${(p) => p.theme.spacing.lg};
  background: ${(p) => p.theme.colors.surfaceAlt};

  .head {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    margin-bottom: ${(p) => p.theme.spacing.sm};
  }

  .shift {
    font-weight: 700;
    font-size: 1rem;
  }

  .pct {
    font-size: 1.6rem;
    font-weight: 700;
    letter-spacing: -0.02em;
  }

  .gaps {
    display: flex;
    gap: ${(p) => p.theme.spacing.lg};
    margin-top: ${(p) => p.theme.spacing.md};
    font-size: 0.82rem;
    color: ${(p) => p.theme.colors.text.secondary};

    strong {
      color: ${(p) => p.theme.colors.text.primary};
      font-variant-numeric: tabular-nums;
    }
  }
`;

const GroupGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: ${(p) => p.theme.spacing.lg};
`;

const GroupCard = styled.div`
  border: 1px solid ${(p) => p.theme.colors.border.light};
  border-radius: ${(p) => p.theme.borderRadius.large};
  padding: ${(p) => p.theme.spacing.lg};

  .g-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: ${(p) => p.theme.spacing.sm};

    .g-name { font-weight: 700; font-size: 1rem; }
    .g-count { font-size: 1.4rem; font-weight: 700; letter-spacing: -0.02em; }
  }

  .g-desc {
    font-size: 0.8rem;
    color: ${(p) => p.theme.colors.text.light};
    margin-bottom: ${(p) => p.theme.spacing.md};
  }

  .dist-row {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 6px;
    font-size: 0.82rem;

    .dist-bar { flex: 1; }
    .dist-n { width: 24px; text-align: right; font-variant-numeric: tabular-nums; font-weight: 600; }
  }

  .g-foot {
    display: flex;
    gap: ${(p) => p.theme.spacing.md};
    margin-top: ${(p) => p.theme.spacing.md};
    flex-wrap: wrap;
  }
`;

const Notice = styled.div`
  padding: ${(p) => p.theme.spacing.md} ${(p) => p.theme.spacing.lg};
  background: ${(p) => p.theme.colors.surfaceAlt};
  border: 1px dashed ${(p) => p.theme.colors.border.medium};
  border-radius: ${(p) => p.theme.borderRadius.medium};
  color: ${(p) => p.theme.colors.text.secondary};
  font-size: 0.9rem;
  margin-bottom: ${(p) => p.theme.spacing.xl};
`;

const CenterState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${(p) => p.theme.spacing.md};
  padding: ${(p) => p.theme.spacing.xxl};
  color: ${(p) => p.theme.colors.text.secondary};
`;

const FootNote = styled.p`
  font-size: 0.78rem;
  color: ${(p) => p.theme.colors.text.light};
  margin-top: ${(p) => p.theme.spacing.lg};
`;

/* ------------------------- search + insight ----------------------- */

const SearchWrap = styled.div`
  position: relative;
  min-width: 260px;
`;

const SearchBox = styled.div`
  position: relative;

  svg {
    position: absolute;
    left: 12px;
    top: 50%;
    transform: translateY(-50%);
    color: ${(p) => p.theme.colors.text.light};
    pointer-events: none;
  }

  input {
    padding-left: 36px;
  }
`;

const Results = styled.div`
  position: absolute;
  z-index: 40;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  background: ${(p) => p.theme.colors.surface};
  border: 1px solid ${(p) => p.theme.colors.border.medium};
  border-radius: ${(p) => p.theme.borderRadius.medium};
  box-shadow: ${(p) => p.theme.shadows.medium};
  max-height: 320px;
  overflow: auto;
`;

const ResultItem = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  border: none;
  background: none;
  text-align: left;
  padding: 9px 12px;
  cursor: pointer;
  font-family: ${(p) => p.theme.fonts.main};
  border-bottom: 1px solid ${(p) => p.theme.colors.border.light};

  &:hover { background: ${(p) => p.theme.colors.surfaceAlt}; }
  &:last-child { border-bottom: none; }

  .r-name { font-weight: 600; color: ${(p) => p.theme.colors.text.primary}; font-size: 0.9rem; }
  .r-sub { font-size: 0.76rem; color: ${(p) => p.theme.colors.text.light}; }
`;

const EmptyResult = styled.div`
  padding: 12px;
  color: ${(p) => p.theme.colors.text.light};
  font-size: 0.85rem;
`;

const IdentityCard = styled(Card)`
  display: flex;
  align-items: center;
  gap: ${(p) => p.theme.spacing.lg};
  flex-wrap: wrap;
  margin-bottom: ${(p) => p.theme.spacing.xl};
`;

const Avatar = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-weight: 700;
  font-size: 1.4rem;
  flex-shrink: 0;
`;

const Identity = styled.div`
  flex: 1;
  min-width: 220px;

  .i-name { font-size: 1.4rem; font-weight: 700; letter-spacing: -0.01em; display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
  .i-meta { margin-top: 6px; display: flex; gap: ${(p) => p.theme.spacing.lg}; flex-wrap: wrap; font-size: 0.85rem; color: ${(p) => p.theme.colors.text.secondary}; }
  .i-meta b { color: ${(p) => p.theme.colors.text.primary}; font-weight: 600; }
`;

const FlagRow = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: ${(p) => p.theme.spacing.md};
`;

const TwoCol = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: ${(p) => p.theme.spacing.xl};
`;

const TrendChart = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 6px;
  height: 150px;
  padding-top: ${(p) => p.theme.spacing.md};

  .t-col { flex: 1; display: flex; flex-direction: column; align-items: center; gap: 6px; height: 100%; justify-content: flex-end; }
  .t-bar-wrap { width: 100%; display: flex; align-items: flex-end; justify-content: center; flex: 1; }
  .t-bar { width: 70%; border-radius: 4px 4px 0 0; min-height: 2px; transition: height 0.3s ease; }
  .t-val { font-size: 0.7rem; font-variant-numeric: tabular-nums; color: ${(p) => p.theme.colors.text.secondary}; font-weight: 600; }
  .t-lbl { font-size: 0.66rem; color: ${(p) => p.theme.colors.text.light}; }
`;

const StatRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 0;
  border-bottom: 1px solid ${(p) => p.theme.colors.border.light};
  font-size: 0.9rem;

  &:last-child { border-bottom: none; }
  .s-label { color: ${(p) => p.theme.colors.text.secondary}; }
  .s-value { font-weight: 700; font-variant-numeric: tabular-nums; }
`;

const BenchWrap = styled.div`
  .b-big { font-size: 2.4rem; font-weight: 700; letter-spacing: -0.02em; line-height: 1; }
  .b-sub { color: ${(p) => p.theme.colors.text.secondary}; font-size: 0.9rem; margin-top: 4px; }
  .b-scale { margin-top: ${(p) => p.theme.spacing.lg}; }
`;

const RiskBanner = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: ${(p) => p.theme.spacing.md} ${(p) => p.theme.spacing.lg};
  border-radius: ${(p) => p.theme.borderRadius.medium};
  background: ${(p) => (p.tone === 'high' ? '#fdf3f1' : p.theme.colors.surfaceAlt)};
  border: 1px solid ${(p) => (p.tone === 'high' ? '#e7c9c3' : p.theme.colors.border.light)};
  margin-bottom: ${(p) => p.theme.spacing.md};

  .rb-big { font-size: 1.8rem; font-weight: 700; line-height: 1; color: ${(p) => (p.tone === 'high' ? '#c5372c' : p.theme.colors.text.primary)}; }
  .rb-text { font-size: 0.85rem; color: ${(p) => p.theme.colors.text.secondary}; }
  svg { color: ${(p) => (p.tone === 'high' ? '#c5372c' : p.theme.colors.text.light)}; flex-shrink: 0; }
`;

const LeaveRow = styled.div`
  margin-bottom: ${(p) => p.theme.spacing.md};

  .lr-head { display: flex; justify-content: space-between; font-size: 0.88rem; margin-bottom: 6px; }
  .lr-name { font-weight: 600; }
  .lr-nums { color: ${(p) => p.theme.colors.text.secondary}; font-variant-numeric: tabular-nums; }
`;

const Timeline = styled.div`
  .tl-item { display: flex; align-items: center; gap: 12px; padding: 8px 0; border-bottom: 1px solid ${(p) => p.theme.colors.border.light}; font-size: 0.88rem; }
  .tl-item:last-child { border-bottom: none; }
  .tl-date { width: 96px; color: ${(p) => p.theme.colors.text.secondary}; font-variant-numeric: tabular-nums; flex-shrink: 0; }
  .tl-branch { flex: 1; font-weight: 600; }
  .tl-shift { font-size: 0.72rem; color: ${(p) => p.theme.colors.text.light}; text-transform: uppercase; letter-spacing: 0.04em; }
`;

/* --------------------------- component ---------------------------- */

const Meter = ({ pct, color }) => (
  <BarTrack>
    <BarFill style={{ width: `${Math.max(0, Math.min(100, pct))}%`, background: color }} />
  </BarTrack>
);

const CategoryTag = ({ role }) => {
  const cat = ROLE_TO_CATEGORY[role];
  const meta = CATEGORY_META[cat];
  return (
    <RoleName>
      <Dot style={{ background: meta ? meta.color : '#9c948a', width: 20, height: 20, fontSize: '0.62rem' }}>
        {meta ? meta.short : '?'}
      </Dot>
      {(role || '').replace(/_/g, ' ')}
    </RoleName>
  );
};

const SummaryPage = () => {
  const now = new Date();
  const [mode, setMode] = useState('month');
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth());
  const [utilRole, setUtilRole] = useState('all');

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [assignments, setAssignments] = useState([]);
  const [users, setUsers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [holidayGroups, setHolidayGroups] = useState([]);
  const [holidays, setHolidays] = useState([]);
  // Coverage gaps for the selected period (approved leave; understaffed slots
  // are derived from the already-loaded period assignments).
  const [gapData, setGapData] = useState({ leaves: [] });
  const [excludedBranchIds, setExcludedBranchIds] = useState([]);

  // Employee Insight (search + per-person deep dive).
  const [query, setQuery] = useState('');
  const [showResults, setShowResults] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [person, setPerson] = useState({ loading: false, error: null, calendar: [], balances: [], requests: [] });

  const rangeInfo = useMemo(() => {
    if (mode === 'year') {
      return { start: `${year}-01-01`, end: `${year}-12-31`, year, label: `${year}` };
    }
    const last = new Date(year, month + 1, 0).getDate();
    return {
      start: `${year}-${pad2(month + 1)}-01`,
      end: `${year}-${pad2(month + 1)}-${pad2(last)}`,
      year,
      label: `${MONTHS[month]} ${year}`,
    };
  }, [mode, year, month]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [assignmentsData, usersRes, branchesRes, groupsRes, holidaysRes, leavesRes] = await Promise.all([
        fetchAllAssignments(rangeInfo.start, rangeInfo.end),
        userAPI.getAll(),
        branchAPI.getAll(),
        holidayGroupAPI.getAll(),
        holidayAPI.getAll({ year: rangeInfo.year }),
        leaveAPI.getLeaveRequests({ status: 'approved', start_date: rangeInfo.start, end_date: rangeInfo.end }),
      ]);
      const allBranches = branchesRes.data || [];
      const excludedIds = new Set(allBranches.filter((b) => isExcludedBranch(b.name)).map((b) => b.id));
      const keep = (a) => !excludedIds.has(a.branch_id);
      setExcludedBranchIds(Array.from(excludedIds));
      setBranches(allBranches.filter((b) => !excludedIds.has(b.id)));
      setAssignments(assignmentsData.filter(keep));
      setUsers(usersRes.data || []);
      setHolidayGroups(groupsRes.data || []);
      setHolidays(holidaysRes.data || []);
      setGapData({ leaves: leavesRes.data || [] });
    } catch (e) {
      console.error('Error loading summary data:', e);
      setError('Failed to load summary data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [rangeInfo]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Load the selected person's 12-month calendar + leave data (privileged-6
  // can read any user; all read-only). Re-runs when the year changes.
  useEffect(() => {
    if (selectedUserId == null) return undefined;
    let cancelled = false;
    (async () => {
      setPerson((p) => ({ ...p, loading: true, error: null }));
      try {
        const [calRes, balRes, reqRes] = await Promise.all([
          workAssignmentAPI.getUserCalendar(selectedUserId, `${year}-01-01`, `${year}-12-31`),
          leaveAPI.getLeaveBalances({ user_id: selectedUserId }),
          leaveAPI.getLeaveRequests({ user_id: selectedUserId }),
        ]);
        if (cancelled) return;
        const cal = ((calRes.data && calRes.data.assignments) || []).filter((a) => !excludedBranchIds.includes(a.branch_id));
        setPerson({
          loading: false,
          error: null,
          calendar: cal,
          balances: balRes.data || [],
          requests: reqRes.data || [],
        });
      } catch (e) {
        if (cancelled) return;
        console.error('Error loading employee insight:', e);
        setPerson({ loading: false, error: 'Failed to load this employee. Please try again.', calendar: [], balances: [], requests: [] });
      }
    })();
    return () => { cancelled = true; };
  }, [selectedUserId, year, excludedBranchIds]);

  const stats = useMemo(() => {
    const branchById = {};
    branches.forEach((b) => { branchById[b.id] = b; });
    const userById = {};
    users.forEach((u) => { userById[u.id] = u; });

    // Slot-level coverage: one entry per (branch, date, shift) that has work.
    const slotMap = new Map();
    assignments.forEach((a) => {
      const cat = ROLE_TO_CATEGORY[a.role];
      const key = `${a.branch_id}|${a.assignment_date}|${a.shift_type}`;
      let s = slotMap.get(key);
      if (!s) {
        s = { branch_id: a.branch_id, shift: a.shift_type, manager: 0, makeup: 0, photographer: 0, retoucher: 0, total: 0 };
        slotMap.set(key, s);
      }
      if (cat) s[cat] += 1;
      s.total += 1;
    });
    const slots = Array.from(slotMap.values());
    const isAdequate = (s) => s.makeup >= 1 && s.photographer >= 1;
    const adequateCount = slots.filter(isAdequate).length;

    // Per-branch aggregation.
    const branchAgg = {};
    branches.forEach((b) => {
      branchAgg[b.id] = { branch: b, assignments: 0, day: 0, noon: 0, staff: new Set(), slots: 0, adequate: 0 };
    });
    assignments.forEach((a) => {
      let ba = branchAgg[a.branch_id];
      if (!ba) {
        ba = { branch: branchById[a.branch_id] || { id: a.branch_id, name: `Branch ${a.branch_id}` }, assignments: 0, day: 0, noon: 0, staff: new Set(), slots: 0, adequate: 0 };
        branchAgg[a.branch_id] = ba;
      }
      ba.assignments += 1;
      if (a.shift_type === 'day') ba.day += 1; else ba.noon += 1;
      ba.staff.add(a.user_id);
    });
    slots.forEach((s) => {
      const ba = branchAgg[s.branch_id];
      if (ba) { ba.slots += 1; if (isAdequate(s)) ba.adequate += 1; }
    });
    const branchRows = Object.values(branchAgg)
      .map((b) => ({
        ...b,
        staffCount: b.staff.size,
        adequacyPct: b.slots ? Math.round((b.adequate / b.slots) * 100) : 0,
        understaffed: b.slots - b.adequate,
      }))
      .sort((x, y) => y.assignments - x.assignments);

    // Per-user aggregation.
    const userAgg = {};
    assignments.forEach((a) => {
      let ua = userAgg[a.user_id];
      if (!ua) {
        ua = { user_id: a.user_id, shifts: 0, day: 0, noon: 0, days: new Set(), branches: new Set(), crossBranch: 0 };
        userAgg[a.user_id] = ua;
      }
      ua.shifts += 1;
      if (a.shift_type === 'day') ua.day += 1; else ua.noon += 1;
      ua.days.add(a.assignment_date);
      ua.branches.add(a.branch_id);
      const home = userById[a.user_id] ? userById[a.user_id].home_branch_id : (a.user ? a.user.home_branch_id : null);
      if (home != null && home !== a.branch_id) ua.crossBranch += 1;
    });

    const roster = users.filter((u) => u.is_active !== false && u.role !== 'admin');
    const utilRows = roster.map((u) => {
      const ua = userAgg[u.id];
      return {
        user: u,
        category: ROLE_TO_CATEGORY[u.role] || 'other',
        shifts: ua ? ua.shifts : 0,
        day: ua ? ua.day : 0,
        noon: ua ? ua.noon : 0,
        daysWorked: ua ? ua.days.size : 0,
        branches: ua ? ua.branches.size : 0,
        crossBranch: ua ? ua.crossBranch : 0,
      };
    }).sort((a, b) => b.shifts - a.shifts);
    const maxShifts = utilRows.reduce((m, r) => Math.max(m, r.shifts), 0);
    const idleCount = utilRows.filter((r) => r.shifts === 0).length;

    // Shift coverage by shift type.
    const coverage = {
      day: { slots: 0, adequate: 0, noMakeup: 0, noPhotographer: 0 },
      noon: { slots: 0, adequate: 0, noMakeup: 0, noPhotographer: 0 },
    };
    slots.forEach((s) => {
      const c = coverage[s.shift];
      if (!c) return;
      c.slots += 1;
      if (isAdequate(s)) c.adequate += 1;
      if (s.makeup === 0) c.noMakeup += 1;
      if (s.photographer === 0) c.noPhotographer += 1;
    });

    // Holiday group balancing.
    const holidayCountByGroup = {};
    holidays.forEach((h) => {
      holidayCountByGroup[h.holiday_group_id] = (holidayCountByGroup[h.holiday_group_id] || 0) + 1;
    });
    const groupRows = holidayGroups.map((g) => {
      const members = users.filter((u) => u.is_active !== false && u.holiday_group_id === g.id);
      const dist = { manager: 0, makeup: 0, photographer: 0, retoucher: 0 };
      let moveable = 0;
      members.forEach((m) => {
        const c = ROLE_TO_CATEGORY[m.role];
        if (c) dist[c] += 1;
        if (m.is_moveable) moveable += 1;
      });
      const maxCat = Math.max(1, dist.manager, dist.makeup, dist.photographer, dist.retoucher);
      return { group: g, memberCount: members.length, dist, maxCat, moveable, holidays: holidayCountByGroup[g.id] || 0 };
    });
    const ungrouped = users.filter((u) => u.is_active !== false && u.role !== 'admin' && u.holiday_group_id == null).length;

    // Moveable staff efficiency.
    const moveableRows = roster.filter((u) => u.is_moveable).map((u) => {
      const ua = userAgg[u.id];
      return {
        user: u,
        shifts: ua ? ua.shifts : 0,
        reach: ua ? ua.branches.size : 0,
        crossBranch: ua ? ua.crossBranch : 0,
        homeShifts: ua ? ua.shifts - ua.crossBranch : 0,
      };
    }).sort((a, b) => b.crossBranch - a.crossBranch || b.shifts - a.shifts);
    const maxReach = moveableRows.reduce((m, r) => Math.max(m, r.reach), 0);
    const underusedMoveable = moveableRows.filter((r) => r.reach <= 1).length;

    // KPIs.
    const activeStaff = new Set(assignments.map((a) => a.user_id)).size;
    const moveableCrossShifts = Object.values(userAgg).reduce((sum, ua) => {
      const u = userById[ua.user_id];
      return sum + (u && u.is_moveable ? ua.crossBranch : 0);
    }, 0);

    return {
      totalAssignments: assignments.length,
      totalSlots: slots.length,
      adequateSlots: adequateCount,
      understaffedSlots: slots.length - adequateCount,
      adequacyPct: slots.length ? Math.round((adequateCount / slots.length) * 100) : 0,
      activeStaff,
      moveableCrossShifts,
      branchRows,
      utilRows,
      maxShifts,
      idleCount,
      coverage,
      groupRows,
      ungrouped,
      moveableRows,
      maxReach,
      underusedMoveable,
      rosterCount: roster.length,
      moveableCount: moveableRows.length,
    };
  }, [assignments, users, branches, holidayGroups, holidays]);

  // Coverage gaps for the SELECTED period (follows the range selector):
  // scheduled shift slots that miss the makeup/photographer rule, plus who is
  // on approved leave in the period (availability heads-up).
  const gaps = useMemo(() => {
    const branchById = {};
    branches.forEach((b) => { branchById[b.id] = b; });
    const userById = {};
    users.forEach((u) => { userById[u.id] = u; });

    // Group the period's assignments into (branch, date, shift) slots.
    const slotMap = new Map();
    assignments.forEach((a) => {
      const c = ROLE_TO_CATEGORY[a.role];
      const k = `${a.branch_id}|${a.assignment_date}|${a.shift_type}`;
      let s = slotMap.get(k);
      if (!s) {
        s = { branch_id: a.branch_id, date: a.assignment_date, shift: a.shift_type, makeup: 0, photographer: 0, names: [] };
        slotMap.set(k, s);
      }
      if (c) s[c] += 1;
      s.names.push({ name: (a.user && a.user.name) || (userById[a.user_id] && userById[a.user_id].name) || 'Staff' });
    });

    const understaffed = Array.from(slotMap.values())
      .filter((s) => s.makeup === 0 || s.photographer === 0)
      .map((s) => ({
        ...s,
        branchName: (branchById[s.branch_id] || {}).name || `Branch ${s.branch_id}`,
        missing: [s.makeup === 0 ? 'makeup' : null, s.photographer === 0 ? 'photographer' : null].filter(Boolean),
      }))
      .sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : a.branchName.localeCompare(b.branchName)));

    // Per-branch rollup so structural cases read as one line.
    const byBranch = {};
    understaffed.forEach((s) => {
      if (!byBranch[s.branch_id]) byBranch[s.branch_id] = { name: s.branchName, count: 0 };
      byBranch[s.branch_id].count += 1;
    });
    const branchSummary = Object.values(byBranch).sort((a, b) => b.count - a.count);

    // Approved leave within the selected period (availability heads-up).
    const leaves = (gapData.leaves || [])
      .filter((r) => r.status === 'approved' && (r.leave_date || '') >= rangeInfo.start && (r.leave_date || '') <= rangeInfo.end)
      .map((r) => {
        const u = userById[r.user_id] || r.user || {};
        return {
          id: r.id,
          date: r.leave_date,
          name: u.name || u.username || 'Staff',
          role: u.role,
          category: ROLE_TO_CATEGORY[u.role] || null,
          homeName: (branchById[u.home_branch_id] || {}).name || '—',
          typeName: r.leave_type ? r.leave_type.name : '',
        };
      })
      .sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));
    const criticalLeaves = leaves.filter((l) => l.category === 'makeup' || l.category === 'photographer').length;

    return { understaffed, branchSummary, leaves, criticalLeaves };
  }, [assignments, gapData, branches, users, rangeInfo]);

  const insight = useMemo(() => {
    if (selectedUserId == null) return null;
    const u = users.find((x) => x.id === selectedUserId);
    if (!u) return null;
    const nowDate = new Date();
    const todayStr = `${nowDate.getFullYear()}-${pad2(nowDate.getMonth() + 1)}-${pad2(nowDate.getDate())}`;
    const category = ROLE_TO_CATEGORY[u.role] || null;
    const home = u.home_branch_id;

    // Period assignments for this user (from org data, matches the range).
    const mine = assignments.filter((a) => a.user_id === selectedUserId);
    const shifts = mine.length;
    const dates = mine.map((a) => a.assignment_date);
    const daysWorked = new Set(dates).size;
    const dayCount = mine.filter((a) => a.shift_type === 'day').length;
    const noonCount = shifts - dayCount;
    const reach = new Set(mine.map((a) => a.branch_id)).size;
    const crossBranch = mine.filter((a) => home != null && a.branch_id !== home).length;
    const maxStreak = maxConsecutive(dates);

    // Key-person risk: slots where this user is the ONLY makeup/photographer.
    const slotCat = new Map();
    assignments.forEach((a) => {
      const c = ROLE_TO_CATEGORY[a.role];
      if (c !== 'makeup' && c !== 'photographer') return;
      const k = `${a.branch_id}|${a.assignment_date}|${a.shift_type}`;
      let s = slotCat.get(k);
      if (!s) { s = { makeup: 0, photographer: 0 }; slotCat.set(k, s); }
      s[c] += 1;
    });
    let soleMakeup = 0;
    let solePhoto = 0;
    mine.forEach((a) => {
      const c = ROLE_TO_CATEGORY[a.role];
      if (c !== 'makeup' && c !== 'photographer') return;
      const s = slotCat.get(`${a.branch_id}|${a.assignment_date}|${a.shift_type}`);
      if (!s) return;
      if (c === 'makeup' && s.makeup === 1) soleMakeup += 1;
      if (c === 'photographer' && s.photographer === 1) solePhoto += 1;
    });
    const soleTotal = soleMakeup + solePhoto;
    const isCritical = category === 'makeup' || category === 'photographer';
    const soleShare = shifts ? Math.round((soleTotal / shifts) * 100) : 0;

    // Peer benchmark among same-category active staff, same period.
    const shiftsByUser = {};
    assignments.forEach((a) => { shiftsByUser[a.user_id] = (shiftsByUser[a.user_id] || 0) + 1; });
    const peers = users.filter((x) => x.is_active !== false && x.role !== 'admin' && ROLE_TO_CATEGORY[x.role] === category);
    const peerList = peers.map((x) => ({ id: x.id, n: shiftsByUser[x.id] || 0 })).sort((a, b) => b.n - a.n);
    const peerCount = peerList.length;
    const peerAvg = peerCount ? peerList.reduce((s, r) => s + r.n, 0) / peerCount : 0;
    const rank = peerList.findIndex((r) => r.id === selectedUserId) + 1;
    const fewerThan = peerList.filter((r) => r.n < shifts).length;
    const percentile = peerCount > 1 ? Math.round((fewerThan / (peerCount - 1)) * 100) : 100;
    const peerMax = peerList.length ? peerList[0].n : 0;

    // Branch distribution (period).
    const branchCount = {};
    mine.forEach((a) => { branchCount[a.branch_id] = (branchCount[a.branch_id] || 0) + 1; });
    const branchDist = Object.entries(branchCount).map(([bid, n]) => {
      const b = branches.find((x) => x.id === Number(bid));
      return { id: Number(bid), name: b ? b.name : `Branch ${bid}`, n, isHome: Number(bid) === home };
    }).sort((a, b) => b.n - a.n);
    const branchMax = branchDist.length ? branchDist[0].n : 0;

    // 12-month trend from the personal calendar (selected year).
    const monthly = Array.from({ length: 12 }, () => 0);
    person.calendar.forEach((a) => {
      const mm = Number((a.assignment_date || '').slice(5, 7)) - 1;
      if (mm >= 0 && mm < 12) monthly[mm] += 1;
    });
    const monthlyMax = monthly.reduce((m, n) => Math.max(m, n), 0);
    const yearTotal = monthly.reduce((s, n) => s + n, 0);

    // Leave (selected year).
    const leaveByType = {};
    person.balances.filter((b) => b.year === year).forEach((b) => {
      const name = b.leave_type ? b.leave_type.name : `Type ${b.leave_type_id}`;
      if (!leaveByType[name]) leaveByType[name] = { name, allocated: 0, used: 0, remaining: 0 };
      leaveByType[name].allocated += b.allocated_days || 0;
      leaveByType[name].used += b.used_days || 0;
      leaveByType[name].remaining += b.remaining_days != null ? b.remaining_days : (b.allocated_days || 0) - (b.used_days || 0);
    });
    const leaveRows = Object.values(leaveByType);
    const reqCounts = { approved: 0, pending: 0, rejected: 0 };
    person.requests.filter((r) => (r.leave_date || '').slice(0, 4) === String(year)).forEach((r) => {
      if (reqCounts[r.status] != null) reqCounts[r.status] += 1;
    });
    const upcoming = person.requests
      .filter((r) => r.status === 'approved' && (r.leave_date || '') >= todayStr)
      .sort((a, b) => (a.leave_date < b.leave_date ? -1 : 1))
      .slice(0, 5);

    // Recent assignments (from personal calendar).
    const recent = [...person.calendar]
      .sort((a, b) => (a.assignment_date < b.assignment_date ? 1 : -1))
      .slice(0, 10)
      .map((a) => ({ ...a, branchName: (branches.find((x) => x.id === a.branch_id) || {}).name || `Branch ${a.branch_id}` }));

    // Executive flags.
    const flags = [];
    if (isCritical && soleTotal >= 5) flags.push({ tone: 'high', label: `Key-person risk · sole cover ${soleTotal}×` });
    if (maxStreak >= 6) flags.push({ tone: 'high', label: `${maxStreak} consecutive days` });
    if (shifts === 0) flags.push({ tone: 'warn', label: 'No shifts this period' });
    else if (peerAvg > 0 && shifts < peerAvg * 0.5) flags.push({ tone: 'warn', label: 'Under-utilized vs peers' });
    else if (peerAvg > 0 && shifts > peerAvg * 1.4) flags.push({ tone: 'high', label: 'Over-utilized vs peers' });
    if (u.is_moveable && reach <= 1 && shifts > 0) flags.push({ tone: 'warn', label: 'Moveable but reached ≤1 branch' });
    if (upcoming.length > 0) flags.push({ tone: 'info', label: `${upcoming.length} upcoming leave` });

    const group = holidayGroups.find((g) => g.id === u.holiday_group_id);

    return {
      u,
      category,
      homeName: (branches.find((x) => x.id === home) || {}).name || '—',
      groupName: group ? group.name : '—',
      shifts, daysWorked, dayCount, noonCount, reach, crossBranch, maxStreak,
      soleMakeup, solePhoto, soleTotal, isCritical, soleShare,
      rank, peerCount, peerAvg, percentile, peerMax,
      branchDist, branchMax,
      monthly, monthlyMax, yearTotal,
      leaveRows, reqCounts, upcoming,
      recent, flags,
    };
  }, [selectedUserId, users, assignments, branches, holidayGroups, person, year]);

  const searchResults = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return users
      .filter((u) => u.is_active !== false && u.role !== 'admin')
      .filter((u) => `${u.name || ''} ${u.username || ''} ${u.email || ''}`.toLowerCase().includes(q))
      .slice(0, 8);
  }, [query, users]);

  const selectPerson = (id) => {
    setSelectedUserId(id);
    setQuery('');
    setShowResults(false);
  };

  const yearOptions = [];
  for (let y = now.getFullYear() + 1; y >= now.getFullYear() - 3; y -= 1) yearOptions.push(y);

  const filteredUtil = stats.utilRows.filter((r) => (utilRole === 'all' ? true : r.category === utilRole));
  const okColor = '#2e7d5b';
  const warnColor = '#b26a12';

  return (
    <PageContainer>
      <PageHeader>
        <HeaderRow>
          <div>
            <h1><FiTrendingUp size={24} /> Summary &amp; Reports</h1>
            <p>Operational analytics for {rangeInfo.label} — read-only, computed live from scheduled work assignments.</p>
          </div>
          <Controls>
            <Field>
              <label>Find employee</label>
              <SearchWrap>
                <SearchBox>
                  <FiSearch size={16} />
                  <ModernInput
                    placeholder="Search by name…"
                    value={query}
                    onChange={(e) => { setQuery(e.target.value); setShowResults(true); }}
                    onFocus={() => setShowResults(true)}
                    onBlur={() => setTimeout(() => setShowResults(false), 150)}
                  />
                </SearchBox>
                {showResults && query.trim() && (
                  <Results>
                    {searchResults.length === 0 ? (
                      <EmptyResult>No matching employee.</EmptyResult>
                    ) : searchResults.map((u) => {
                      const cm = CATEGORY_META[ROLE_TO_CATEGORY[u.role]] || {};
                      return (
                        <ResultItem key={u.id} onMouseDown={() => selectPerson(u.id)}>
                          <Dot style={{ background: cm.color || '#9c948a', width: 22, height: 22, fontSize: '0.62rem' }}>{cm.short || '?'}</Dot>
                          <div>
                            <div className="r-name">{u.name || u.username}</div>
                            <div className="r-sub">{(u.role || '').replace(/_/g, ' ')}{u.home_branch ? ` · ${u.home_branch.name}` : ''}</div>
                          </div>
                        </ResultItem>
                      );
                    })}
                  </Results>
                )}
              </SearchWrap>
            </Field>
            <Field>
              <label>Range</label>
              <Segmented>
                <button type="button" className={mode === 'month' ? 'active' : ''} onClick={() => setMode('month')}>Month</button>
                <button type="button" className={mode === 'year' ? 'active' : ''} onClick={() => setMode('year')}>Year</button>
              </Segmented>
            </Field>
            {mode === 'month' && (
              <Field>
                <label>Month</label>
                <ModernSelect value={month} onChange={(e) => setMonth(Number(e.target.value))} style={{ width: 'auto' }}>
                  {MONTHS.map((m, i) => <option key={m} value={i}>{m}</option>)}
                </ModernSelect>
              </Field>
            )}
            <Field>
              <label>Year</label>
              <ModernSelect value={year} onChange={(e) => setYear(Number(e.target.value))} style={{ width: 'auto' }}>
                {yearOptions.map((y) => <option key={y} value={y}>{y}</option>)}
              </ModernSelect>
            </Field>
            <ModernButton variant="secondary" onClick={fetchData} disabled={loading}>
              <FiRefreshCw size={16} /> Refresh
            </ModernButton>
          </Controls>
        </HeaderRow>
      </PageHeader>

      {selectedUserId != null ? (
        <>
          <div style={{ marginBottom: 16 }}>
            <ModernButton variant="outline" onClick={() => { setSelectedUserId(null); setPerson({ loading: false, error: null, calendar: [], balances: [], requests: [] }); }}>
              <FiArrowLeft size={16} /> Back to overview
            </ModernButton>
          </div>
          {(loading || person.loading) ? (
            <CenterState><LoadingSpinner size="40px" /><div>Loading employee insight…</div></CenterState>
          ) : person.error ? (
            <CenterState><div style={{ color: '#c5372c', fontWeight: 600 }}>{person.error}</div><ModernButton onClick={() => setSelectedUserId((v) => v)}>Try again</ModernButton></CenterState>
          ) : !insight ? (
            <CenterState><div>Employee not found in the current roster.</div></CenterState>
          ) : (() => {
            const ins = insight;
            const catMeta = CATEGORY_META[ins.category] || { color: '#9c948a', label: '—', short: '?' };
            const name = ins.u.name || ins.u.username;
            const initials = (name || '?').split(' ').filter(Boolean).map((s) => s[0]).slice(0, 2).join('').toUpperCase();
            const vsAvg = ins.peerAvg > 0 ? Math.round(((ins.shifts - ins.peerAvg) / ins.peerAvg) * 100) : 0;
            return (
              <>
                <IdentityCard>
                  <Avatar style={{ background: catMeta.color }}>{initials}</Avatar>
                  <Identity>
                    <div className="i-name">{name}{ins.u.is_active === false && <Badge>inactive</Badge>}</div>
                    <div className="i-meta">
                      <span><CategoryTag role={ins.u.role} /></span>
                      <span><b>Home</b> {ins.homeName}</span>
                      <span><b>Group</b> {ins.groupName}</span>
                      <span><b>Tenure</b> {tenureLabel(ins.u.created_at)}</span>
                      {ins.u.is_moveable && <Badge variant="info">moveable</Badge>}
                      {ins.u.email && <span>{ins.u.email}</span>}
                    </div>
                    {ins.flags.length > 0 && (
                      <FlagRow>
                        {ins.flags.map((f, i) => (
                          <Badge key={i} variant={f.tone === 'high' ? 'error' : f.tone === 'warn' ? 'warning' : 'info'}>{f.label}</Badge>
                        ))}
                      </FlagRow>
                    )}
                  </Identity>
                </IdentityCard>

                <KpiGrid>
                  <StatCard><span className="value">{fmt(ins.shifts)}</span><span className="label">Shifts · {rangeInfo.label}</span><span className="sub">{fmt(ins.daysWorked)} days worked</span></StatCard>
                  <StatCard><span className="value">{ins.dayCount} / {ins.noonCount}</span><span className="label">Day / Noon</span><span className="sub">shift split</span></StatCard>
                  <StatCard><span className="value">{fmt(ins.reach)}</span><span className="label">Branches reached</span><span className="sub">{fmt(ins.crossBranch)} cross-branch shifts</span></StatCard>
                  <StatCard accent={ins.maxStreak >= 6 ? '#c5372c' : undefined}><span className="value">{ins.maxStreak}</span><span className="label">Max consecutive days</span><span className="sub">6-day limit rule</span></StatCard>
                  <StatCard><span className="value">{ins.peerCount ? `#${ins.rank}` : '—'}</span><span className="label">Rank · {catMeta.label}</span><span className="sub">of {fmt(ins.peerCount)} peers</span></StatCard>
                  <StatCard accent={ins.isCritical && ins.soleTotal >= 5 ? '#c5372c' : undefined}><span className="value">{ins.isCritical ? fmt(ins.soleTotal) : '—'}</span><span className="label">Sole-cover shifts</span><span className="sub">{ins.isCritical ? `${ins.soleShare}% key-person` : 'non-critical role'}</span></StatCard>
                </KpiGrid>

                <TwoCol>
                  <div>
                    <Section>
                      <SectionHead><h2><FiTrendingUp size={18} /> Workload trend · {rangeInfo.year}</h2></SectionHead>
                      <SectionSub>Shifts per month ({fmt(ins.yearTotal)} total this year).</SectionSub>
                      <TrendChart>
                        {ins.monthly.map((n, i) => (
                          <div className="t-col" key={i}>
                            <div className="t-val">{n || ''}</div>
                            <div className="t-bar-wrap"><div className="t-bar" style={{ height: ins.monthlyMax ? `${Math.max(2, (n / ins.monthlyMax) * 100)}%` : '2px', background: catMeta.color }} /></div>
                            <div className="t-lbl">{MONTHS[i][0]}</div>
                          </div>
                        ))}
                      </TrendChart>
                    </Section>

                    <Section>
                      <SectionHead><h2><FiMapPin size={18} /> Where they work · {rangeInfo.label}</h2></SectionHead>
                      <SectionSub>Shift distribution across branches (home highlighted).</SectionSub>
                      {ins.branchDist.length === 0 ? <div style={{ color: '#9c948a' }}>No shifts in this period.</div> : ins.branchDist.map((b) => (
                        <div key={b.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 0', borderBottom: '1px solid #e7e3db' }}>
                          <span style={{ width: 120, fontWeight: 600 }}>{b.name}{b.isHome && <Badge style={{ marginLeft: 6 }}>home</Badge>}</span>
                          <div style={{ flex: 1 }}><Meter pct={ins.branchMax ? (b.n / ins.branchMax) * 100 : 0} color={b.isHome ? '#930c00' : '#3a6ea5'} /></div>
                          <span style={{ width: 32, textAlign: 'right', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{b.n}</span>
                        </div>
                      ))}
                    </Section>
                  </div>

                  <div>
                    <Section>
                      <SectionHead><h2><FiStar size={18} /> Peer benchmark</h2></SectionHead>
                      <SectionSub>Versus active {catMeta.label.toLowerCase()} staff in {rangeInfo.label}.</SectionSub>
                      <BenchWrap>
                        <div className="b-big" style={{ color: ins.peerCount > 1 ? (vsAvg >= 0 ? '#2e7d5b' : '#c5372c') : '#6b645c' }}>
                          {ins.peerCount > 1 && ins.shifts > 0 ? `${vsAvg >= 0 ? '+' : ''}${vsAvg}%` : '—'}
                        </div>
                        <div className="b-sub">vs peer average · rank #{ins.rank} of {fmt(ins.peerCount)} {catMeta.label.toLowerCase()}s</div>
                        <div className="b-scale">
                          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.78rem', color: '#6b645c', marginBottom: 6 }}>
                            <span>You {ins.shifts}</span><span>Avg {ins.peerAvg.toFixed(1)}</span><span>Top {ins.peerMax}</span>
                          </div>
                          <Meter pct={ins.peerMax ? (ins.shifts / ins.peerMax) * 100 : 0} color={catMeta.color} />
                        </div>
                      </BenchWrap>
                    </Section>

                    <Section>
                      <SectionHead><h2><FiAlertTriangle size={18} /> Key-person risk</h2></SectionHead>
                      {ins.isCritical ? (
                        <>
                          <RiskBanner tone={ins.soleTotal >= 5 ? 'high' : 'normal'}>
                            <FiAlertTriangle size={24} />
                            <div>
                              <div className="rb-big">{ins.soleTotal}</div>
                              <div className="rb-text">shifts where {name} was the <b>only</b> {catMeta.label.toLowerCase()} covering that branch &amp; shift ({ins.soleShare}% of their shifts).</div>
                            </div>
                          </RiskBanner>
                          <div style={{ fontSize: '0.82rem', color: '#6b645c' }}>Sole makeup: <b>{ins.soleMakeup}</b> · sole photographer: <b>{ins.solePhoto}</b>. {ins.soleTotal >= 5 ? 'High dependency — an absence directly risks coverage. Consider cross-training or pairing.' : 'Low dependency.'}</div>
                        </>
                      ) : (
                        <div style={{ color: '#6b645c', fontSize: '0.88rem' }}>Coverage risk is tracked for the critical roles (makeup / photographer). {name} is a {catMeta.label.toLowerCase()}, so this metric does not apply.</div>
                      )}
                    </Section>

                    <Section>
                      <SectionHead><h2><FiCalendar size={18} /> Leave &amp; availability · {rangeInfo.year}</h2></SectionHead>
                      {ins.leaveRows.length === 0 ? (
                        <div style={{ color: '#9c948a', marginBottom: 12 }}>No leave quota configured for {rangeInfo.year}.</div>
                      ) : ins.leaveRows.map((l) => (
                        <LeaveRow key={l.name}>
                          <div className="lr-head"><span className="lr-name">{l.name}</span><span className="lr-nums">{l.used}/{l.allocated} used · {l.remaining} left</span></div>
                          <Meter pct={l.allocated ? (l.used / l.allocated) * 100 : 0} color={l.allocated && l.used / l.allocated > 0.85 ? '#c5372c' : '#2e7d5b'} />
                        </LeaveRow>
                      ))}
                      <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
                        <Badge variant="success">{ins.reqCounts.approved} approved</Badge>
                        <Badge variant="warning">{ins.reqCounts.pending} pending</Badge>
                        <Badge variant="error">{ins.reqCounts.rejected} rejected</Badge>
                      </div>
                      {ins.upcoming.length > 0 && (
                        <div style={{ marginTop: 14 }}>
                          <div style={{ fontSize: '0.72rem', fontWeight: 600, color: '#6b645c', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 6 }}>Upcoming approved leave</div>
                          {ins.upcoming.map((r) => (
                            <div key={r.id} style={{ fontSize: '0.85rem', padding: '3px 0', display: 'flex', justifyContent: 'space-between' }}>
                              <span>{r.leave_date}</span><span style={{ color: '#6b645c' }}>{r.leave_type ? r.leave_type.name : ''}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </Section>
                  </div>
                </TwoCol>

                <Section>
                  <SectionHead><h2><FiClock size={18} /> Recent assignments</h2></SectionHead>
                  <SectionSub>Last 10 scheduled shifts in {rangeInfo.year}.</SectionSub>
                  <Timeline>
                    {ins.recent.length === 0 ? <div style={{ color: '#9c948a' }}>No assignments in {rangeInfo.year}.</div> : ins.recent.map((a, i) => (
                      <div className="tl-item" key={a.id || i}>
                        <span className="tl-date">{a.assignment_date}</span>
                        <span className="tl-branch">{a.branchName}</span>
                        <CategoryTag role={a.role} />
                        <span className="tl-shift">{a.shift_type}</span>
                      </div>
                    ))}
                  </Timeline>
                </Section>

                <FootNote>Read-only employee insight. Period metrics (shifts, benchmark, key-person) follow the range selector; the workload trend and leave figures use calendar year {rangeInfo.year}. "Sole-cover" counts branch/shift slots where this person was the only makeup or photographer scheduled.</FootNote>
              </>
            );
          })()}
        </>
      ) : loading ? (
        <CenterState>
          <LoadingSpinner size="40px" />
          <div>Crunching {rangeInfo.label}…</div>
        </CenterState>
      ) : error ? (
        <CenterState>
          <div style={{ color: '#c5372c', fontWeight: 600 }}>{error}</div>
          <ModernButton onClick={fetchData}>Try again</ModernButton>
        </CenterState>
      ) : (
        <>
          <KpiGrid>
            <StatCard>
              <span className="value">{fmt(stats.totalAssignments)}</span>
              <span className="label">Assignments</span>
              <span className="sub">{fmt(stats.totalSlots)} scheduled shift slots</span>
            </StatCard>
            <StatCard accent={stats.adequacyPct >= 80 ? okColor : warnColor}>
              <span className="value">{stats.adequacyPct}%</span>
              <span className="label">Shift coverage met</span>
              <span className="sub">{fmt(stats.adequateSlots)} of {fmt(stats.totalSlots)} slots</span>
            </StatCard>
            <StatCard accent={stats.understaffedSlots > 0 ? '#c5372c' : okColor}>
              <span className="value">{fmt(stats.understaffedSlots)}</span>
              <span className="label">Understaffed slots</span>
              <span className="sub">missing makeup or photographer</span>
            </StatCard>
            <StatCard>
              <span className="value">{fmt(stats.activeStaff)}</span>
              <span className="label">Staff scheduled</span>
              <span className="sub">of {fmt(stats.rosterCount)} active ({fmt(stats.idleCount)} idle)</span>
            </StatCard>
            <StatCard>
              <span className="value">{fmt(stats.branchRows.filter((b) => b.assignments > 0).length)}</span>
              <span className="label">Branches operating</span>
              <span className="sub">of {fmt(branches.length)} total</span>
            </StatCard>
            <StatCard>
              <span className="value">{fmt(stats.moveableCrossShifts)}</span>
              <span className="label">Cross-branch shifts</span>
              <span className="sub">by {fmt(stats.moveableCount)} moveable staff</span>
            </StatCard>
          </KpiGrid>

          {/* 0. Coverage gaps — follows the selected period ----------- */}
          <Section>
            <SectionHead><h2><FiAlertTriangle size={18} /> Coverage gaps · {rangeInfo.label}</h2></SectionHead>
            <SectionSub>Scheduled shifts in {rangeInfo.label} that miss the ≥1 makeup + ≥1 photographer rule, plus staff on approved leave during the period. Pick a future month above to plan ahead.</SectionSub>
            {gaps.understaffed.length === 0 && gaps.leaves.length === 0 ? (
              <div style={{ padding: '8px 0', color: '#2e7d5b', fontWeight: 600 }}>No understaffed shifts or approved leave in {rangeInfo.label}. ✔</div>
            ) : (
              <>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
                  <Badge variant={gaps.understaffed.length ? 'error' : 'success'}>{fmt(gaps.understaffed.length)} understaffed shifts</Badge>
                  <Badge variant={gaps.criticalLeaves ? 'warning' : 'default'}>{fmt(gaps.criticalLeaves)} critical-role on leave</Badge>
                  {gaps.branchSummary.map((b) => <Badge key={b.name}>{b.name}: {b.count}</Badge>)}
                </div>
                <TwoCol>
                  <div>
                    <div style={{ fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', color: '#6b645c', marginBottom: 8 }}>Understaffed shifts</div>
                    {gaps.understaffed.length === 0 ? (
                      <div style={{ color: '#2e7d5b' }}>None — every scheduled shift is covered. ✔</div>
                    ) : (
                      <TableWrap maxHeight="360px">
                        <Table>
                          <thead><tr><th>Date</th><th>Branch</th><th>Shift</th><th>Missing</th><th>Scheduled</th></tr></thead>
                          <tbody>
                            {gaps.understaffed.slice(0, 60).map((s, i) => (
                              <tr key={i}>
                                <td>{s.date}</td>
                                <td>{s.branchName}</td>
                                <td style={{ textTransform: 'capitalize' }}>{s.shift}</td>
                                <td>{s.missing.map((m) => <Badge key={m} variant="error" style={{ marginRight: 4 }}>{m}</Badge>)}</td>
                                <td style={{ color: '#6b645c', fontSize: '0.82rem', whiteSpace: 'normal', maxWidth: 220 }}>{s.names.map((n) => n.name).join(', ') || '—'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </TableWrap>
                    )}
                    {gaps.understaffed.length > 60 && <div style={{ fontSize: '0.78rem', color: '#9c948a', marginTop: 6 }}>+{fmt(gaps.understaffed.length - 60)} more (narrow the window by fixing the earliest first).</div>}
                  </div>
                  <div>
                    <div style={{ fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', color: '#6b645c', marginBottom: 8 }}>Approved leave · availability</div>
                    {gaps.leaves.length === 0 ? (
                      <div style={{ color: '#6b645c' }}>No approved leave in {rangeInfo.label}.</div>
                    ) : (
                      <TableWrap maxHeight="360px">
                        <Table>
                          <thead><tr><th>Date</th><th>Employee</th><th>Role</th><th>Home</th><th>Type</th></tr></thead>
                          <tbody>
                            {gaps.leaves.slice(0, 60).map((l) => {
                              const crit = l.category === 'makeup' || l.category === 'photographer';
                              return (
                                <tr key={l.id} style={crit ? { background: '#fbf4e6' } : undefined}>
                                  <td>{l.date}</td>
                                  <td>{l.name}</td>
                                  <td><CategoryTag role={l.role} /></td>
                                  <td>{l.homeName}</td>
                                  <td style={{ color: '#6b645c', fontSize: '0.82rem' }}>{l.typeName}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </Table>
                      </TableWrap>
                    )}
                  </div>
                </TwoCol>
              </>
            )}
          </Section>

          {stats.totalAssignments === 0 && (
            <Notice>No work assignments were scheduled during {rangeInfo.label}. Roster-based sections below still apply; workload metrics will populate once shifts are assigned.</Notice>
          )}

          {/* 1. Branch performance ------------------------------------ */}
          <Section>
            <SectionHead><h2><FiMapPin size={18} /> Branch performance</h2></SectionHead>
            <SectionSub>Workload and staffing adequacy per branch. Coverage = share of scheduled shift slots that met the ≥1 makeup + ≥1 photographer rule.</SectionSub>
            <TableWrap>
              <Table>
                <thead>
                  <tr>
                    <th>Branch</th>
                    <th className="num">Assignments</th>
                    <th className="num">Staff</th>
                    <th className="num">Day</th>
                    <th className="num">Noon</th>
                    <th className="num">Slots</th>
                    <th style={{ minWidth: 160 }}>Coverage met</th>
                    <th className="num">Understaffed</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.branchRows.map((b) => (
                    <tr key={b.branch.id}>
                      <td>{b.branch.name}{b.branch.is_active === false && <Badge style={{ marginLeft: 8 }}>inactive</Badge>}</td>
                      <td className="num">{fmt(b.assignments)}</td>
                      <td className="num">{fmt(b.staffCount)}</td>
                      <td className="num">{fmt(b.day)}</td>
                      <td className="num">{fmt(b.noon)}</td>
                      <td className="num">{fmt(b.slots)}</td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <Meter pct={b.adequacyPct} color={b.adequacyPct >= 80 ? okColor : b.adequacyPct >= 50 ? warnColor : '#c5372c'} />
                          <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600, width: 38, textAlign: 'right' }}>{b.slots ? `${b.adequacyPct}%` : '—'}</span>
                        </div>
                      </td>
                      <td className="num">{b.understaffed > 0 ? <Badge variant="error">{b.understaffed}</Badge> : <span style={{ color: okColor }}>0</span>}</td>
                    </tr>
                  ))}
                  {stats.branchRows.length === 0 && <tr><td colSpan={8} style={{ color: '#9c948a' }}>No branches.</td></tr>}
                </tbody>
              </Table>
            </TableWrap>
          </Section>

          {/* 2. Employee utilization ---------------------------------- */}
          <Section>
            <SectionHead><h2><FiUsers size={18} /> Employee utilization</h2></SectionHead>
            <SectionSub>Shifts worked per active staff member in {rangeInfo.label}. {fmt(stats.idleCount)} with no shifts are highlighted.</SectionSub>
            <Legend>
              {Object.entries(CATEGORY_META).map(([key, meta]) => (
                <div className="item" key={key}>
                  <Dot style={{ background: meta.color, width: 18, height: 18, fontSize: '0.6rem' }}>{meta.short}</Dot>
                  {meta.label}
                </div>
              ))}
              <div style={{ marginLeft: 'auto' }}>
                <ModernSelect value={utilRole} onChange={(e) => setUtilRole(e.target.value)} style={{ width: 'auto' }}>
                  <option value="all">All roles</option>
                  {Object.entries(CATEGORY_META).map(([key, meta]) => <option key={key} value={key}>{meta.label}</option>)}
                </ModernSelect>
              </div>
            </Legend>
            <TableWrap maxHeight="440px">
              <Table>
                <thead>
                  <tr>
                    <th>Employee</th>
                    <th>Role</th>
                    <th>Home branch</th>
                    <th style={{ minWidth: 150 }}>Shifts</th>
                    <th className="num">Days</th>
                    <th className="num">Day</th>
                    <th className="num">Noon</th>
                    <th className="num">Branches</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUtil.map((r) => (
                    <tr key={r.user.id} className={r.shifts === 0 ? 'idle' : ''}>
                      <td>{r.user.name || r.user.username}</td>
                      <td><CategoryTag role={r.user.role} /></td>
                      <td>{r.user.home_branch ? r.user.home_branch.name : '—'}{r.user.is_moveable && <Badge variant="info" style={{ marginLeft: 8 }}>moveable</Badge>}</td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <Meter pct={stats.maxShifts ? (r.shifts / stats.maxShifts) * 100 : 0} color={CATEGORY_META[r.category] ? CATEGORY_META[r.category].color : '#9c948a'} />
                          <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600, width: 28, textAlign: 'right' }}>{r.shifts}</span>
                        </div>
                      </td>
                      <td className="num">{r.daysWorked}</td>
                      <td className="num">{r.day}</td>
                      <td className="num">{r.noon}</td>
                      <td className="num">{r.branches}</td>
                    </tr>
                  ))}
                  {filteredUtil.length === 0 && <tr><td colSpan={8} style={{ color: '#9c948a' }}>No staff match this filter.</td></tr>}
                </tbody>
              </Table>
            </TableWrap>
          </Section>

          {/* 3. Shift coverage ---------------------------------------- */}
          <Section>
            <SectionHead><h2><FiClock size={18} /> Shift coverage</h2></SectionHead>
            <SectionSub>How reliably each shift met the staffing rule across all branches. Gaps count scheduled slots missing that role entirely.</SectionSub>
            <CoverageGrid>
              {['day', 'noon'].map((shift) => {
                const c = stats.coverage[shift];
                const pct = c.slots ? Math.round((c.adequate / c.slots) * 100) : 0;
                return (
                  <CoverageCard key={shift}>
                    <div className="head">
                      <span className="shift">{shift === 'day' ? 'Day shift' : 'Noon shift'}</span>
                      <span className="pct" style={{ color: pct >= 80 ? okColor : pct >= 50 ? warnColor : '#c5372c' }}>{c.slots ? `${pct}%` : '—'}</span>
                    </div>
                    <Meter pct={pct} color={pct >= 80 ? okColor : pct >= 50 ? warnColor : '#c5372c'} />
                    <div className="gaps">
                      <span><strong>{fmt(c.adequate)}</strong> / {fmt(c.slots)} slots met</span>
                    </div>
                    <div className="gaps">
                      <span>Missing makeup: <strong>{fmt(c.noMakeup)}</strong></span>
                      <span>Missing photographer: <strong>{fmt(c.noPhotographer)}</strong></span>
                    </div>
                  </CoverageCard>
                );
              })}
            </CoverageGrid>
          </Section>

          {/* 4. Holiday group balancing ------------------------------- */}
          <Section>
            <SectionHead><h2><FiGift size={18} /> Holiday group balancing</h2></SectionHead>
            <SectionSub>Workforce distribution across holiday groups — watch for lopsided makeup/photographer coverage between groups.{stats.ungrouped > 0 ? ` ${fmt(stats.ungrouped)} active staff have no group assigned.` : ''}</SectionSub>
            <GroupGrid>
              {stats.groupRows.map((g) => (
                <GroupCard key={g.group.id}>
                  <div className="g-head">
                    <span className="g-name">Group {g.group.name}</span>
                    <span className="g-count">{fmt(g.memberCount)}</span>
                  </div>
                  {g.group.description && <div className="g-desc">{g.group.description}</div>}
                  {Object.entries(CATEGORY_META).map(([key, meta]) => (
                    <div className="dist-row" key={key}>
                      <Dot style={{ background: meta.color, width: 18, height: 18, fontSize: '0.6rem' }}>{meta.short}</Dot>
                      <div className="dist-bar">
                        <Meter pct={(g.dist[key] / g.maxCat) * 100} color={meta.color} />
                      </div>
                      <span className="dist-n">{g.dist[key]}</span>
                    </div>
                  ))}
                  <div className="g-foot">
                    <Badge variant="info">{fmt(g.moveable)} moveable</Badge>
                    <Badge>{fmt(g.holidays)} holidays {rangeInfo.year}</Badge>
                  </div>
                </GroupCard>
              ))}
              {stats.groupRows.length === 0 && <div style={{ color: '#9c948a' }}>No holiday groups defined.</div>}
            </GroupGrid>
          </Section>

          {/* 5. Moveable staff efficiency ----------------------------- */}
          <Section>
            <SectionHead><h2><FiNavigation size={18} /> Moveable staff efficiency</h2></SectionHead>
            <SectionSub>How widely moveable staff are deployed. Reach = distinct branches covered; {fmt(stats.underusedMoveable)} moveable staff reached ≤1 branch this period.</SectionSub>
            <TableWrap maxHeight="380px">
              <Table>
                <thead>
                  <tr>
                    <th>Employee</th>
                    <th>Role</th>
                    <th>Home branch</th>
                    <th className="num">Shifts</th>
                    <th style={{ minWidth: 140 }}>Branch reach</th>
                    <th className="num">Cross-branch</th>
                    <th className="num">At home</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.moveableRows.map((r) => (
                    <tr key={r.user.id} className={r.shifts === 0 ? 'idle' : ''}>
                      <td>{r.user.name || r.user.username}</td>
                      <td><CategoryTag role={r.user.role} /></td>
                      <td>{r.user.home_branch ? r.user.home_branch.name : '—'}</td>
                      <td className="num">{fmt(r.shifts)}</td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <Meter pct={stats.maxReach ? (r.reach / stats.maxReach) * 100 : 0} color={r.reach <= 1 ? warnColor : '#3a6ea5'} />
                          <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600, width: 22, textAlign: 'right' }}>{r.reach}</span>
                        </div>
                      </td>
                      <td className="num">{fmt(r.crossBranch)}</td>
                      <td className="num">{fmt(r.homeShifts)}</td>
                    </tr>
                  ))}
                  {stats.moveableRows.length === 0 && <tr><td colSpan={7} style={{ color: '#9c948a' }}>No moveable staff on the roster.</td></tr>}
                </tbody>
              </Table>
            </TableWrap>
          </Section>

          <FootNote>
            All figures are read-only and recomputed from live data each time you change the range or refresh. Coverage uses the app's staffing rule (each shift needs at least one makeup artist and one photographer). "Scheduled slots" count only branch/date/shift combinations that actually have assignments.{EXCLUDED_BRANCH_NAMES.length > 0 && ` Placeholder branch${EXCLUDED_BRANCH_NAMES.length > 1 ? 'es' : ''} excluded from all figures: ${EXCLUDED_BRANCH_NAMES.join(', ')}.`}
          </FootNote>
        </>
      )}
    </PageContainer>
  );
};

export default SummaryPage;
